import { useEffect, useMemo, useState } from "react";
import { CalendarClock } from "lucide-react";
import { PRIORITY_LABEL, STATUS_LABEL, TASK_STATUSES } from "@desk/contracts";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Kbd } from "@/components/ui/input";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { useTaskActions } from "@/data/actions";
import { useHierarchy } from "@/data/hooks";
import { formatInstant, snoozePresets, toIsoUtc } from "@/lib/dates";
import { ui, useUi } from "@/features/ui-state";
import { CustomSnoozeDialog } from "@/features/snooze/SnoozeControls";
import { MoveDialog } from "@/features/destination/MoveDialog";
import { PRIORITY_KEYS, PRIORITY_ORDER } from "@/features/task/TaskMenu";

/** Keyboard-opened menus (s / p / h / m) anchored to the selected row. */
export function QuickMenu() {
  const menu = useUi((s) => s.quickMenu);
  const a = useTaskActions();
  const h = useHierarchy();
  const [custom, setCustom] = useState<typeof menu>(null);
  const [move, setMove] = useState<typeof menu>(null);
  const close = () => ui.set({ quickMenu: null });

  const anchor = useMemo(() => {
    if (!menu) return null;
    const el = document.querySelector<HTMLElement>(`[data-task-id="${CSS.escape(menu.task.id)}"]`);
    return { current: { getBoundingClientRect: () => el?.getBoundingClientRect() ?? new DOMRect(window.innerWidth / 2, 120, 0, 0) } };
  }, [menu]);

  useEffect(() => {
    if (menu?.kind === "move") {
      setMove(menu);
      ui.set({ quickMenu: null });
    }
  }, [menu]);

  const t = menu?.task;
  return (
    <>
      <Popover open={!!menu && menu.kind !== "move"} onOpenChange={(o) => !o && close()}>
        {anchor && <PopoverAnchor virtualRef={anchor as never} />}
        <PopoverContent align="start" className="w-[260px] p-0">
          {t && menu && (
            <Command loop>
              <CommandInput
                autoFocus
                placeholder={menu.kind === "status" ? "Change status…" : menu.kind === "priority" ? "Set priority…" : "Snooze until…"}
                onKeyDown={(e) => {
                  if (menu.kind === "priority" && /^[0-4]$/.test(e.key)) {
                    const p = PRIORITY_ORDER.find((x) => PRIORITY_KEYS[x] === e.key);
                    if (p) {
                      e.preventDefault();
                      void a.setPriority(t, p);
                      close();
                    }
                  }
                }}
              />
              <CommandList>
                <CommandGroup>
                  {menu.kind === "status" &&
                    TASK_STATUSES.map((s) => (
                      <CommandItem key={s} value={STATUS_LABEL[s]} onSelect={() => (void a.setStatus(t, s), close())}>
                        <StatusIcon status={s} />
                        <span className="flex-1">{STATUS_LABEL[s]}</span>
                        {t.status === s && <span className="text-[12px] text-subtle">Current</span>}
                      </CommandItem>
                    ))}
                  {menu.kind === "priority" &&
                    PRIORITY_ORDER.map((p) => (
                      <CommandItem key={p} value={PRIORITY_LABEL[p]} onSelect={() => (void a.setPriority(t, p), close())}>
                        <PriorityIcon priority={p} />
                        <span className="flex-1">{PRIORITY_LABEL[p]}</span>
                        <Kbd>{PRIORITY_KEYS[p]}</Kbd>
                      </CommandItem>
                    ))}
                  {menu.kind === "snooze" && (
                    <>
                      {snoozePresets(h.timezone).map((p) => (
                        <CommandItem key={p.key} value={p.label} onSelect={() => (void a.snooze(t, toIsoUtc(p.at)), close())}>
                          <span className="flex-1">{p.label}</span>
                          <span className="text-[12px] text-subtle">{formatInstant(p.at.toISO()!, h.timezone)}</span>
                        </CommandItem>
                      ))}
                      <CommandItem value="Custom" onSelect={() => (setCustom(menu), close())}>
                        <CalendarClock />
                        Custom…
                      </CommandItem>
                    </>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
      {custom && (
        <CustomSnoozeDialog open onOpenChange={(o) => !o && setCustom(null)} onConfirm={(iso) => void a.snooze(custom.task, iso)} />
      )}
      {move && <MoveDialog task={move.task} open onOpenChange={(o) => !o && setMove(null)} />}
    </>
  );
}
